import { create } from 'zustand';
import type { AchievementId } from '@the-wall/shared';

export type SnackbarType = 'success' | 'error' | 'info' | 'warning';

export interface Snackbar {
  id: string;
  message: string;
  type: SnackbarType;
  achievementId?: AchievementId;
  duration?: number;
  isExiting?: boolean;
}

interface SnackbarState {
  snackbars: Snackbar[];
  addSnackbar: (
    message: string,
    type?: SnackbarType,
    duration?: number,
    options?: { achievementId?: AchievementId }
  ) => void;
  removeSnackbar: (id: string) => void;
  startExitAnimation: (id: string) => void;
}

let snackbarId = 0;
const EXIT_ANIMATION_DURATION = 300; // ms

export const useSnackbarStore = create<SnackbarState>((set, get) => ({
  snackbars: [],

  addSnackbar: (message, type = 'info', duration = 5000, options) => {
    const id = `snackbar-${++snackbarId}`;
    const snackbar: Snackbar = {
      id,
      message,
      type,
      duration,
      isExiting: false,
    };

    if (options?.achievementId) {
      snackbar.achievementId = options.achievementId;
    }

    set((state) => ({
      snackbars: [...state.snackbars, snackbar],
    }));

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        get().startExitAnimation(id);
      }, duration);
    }
  },

  // Start exit animation, then remove after animation completes
  startExitAnimation: (id) => {
    set((state) => ({
      snackbars: state.snackbars.map((s) => (s.id === id ? { ...s, isExiting: true } : s)),
    }));

    // Remove from DOM after animation completes
    setTimeout(() => {
      set((state) => ({
        snackbars: state.snackbars.filter((s) => s.id !== id),
      }));
    }, EXIT_ANIMATION_DURATION);
  },

  removeSnackbar: (id) => {
    // Trigger exit animation instead of immediate removal
    get().startExitAnimation(id);
  },
}));

// Helper function to show snackbars from anywhere
export const showSnackbar = (
  message: string,
  type: SnackbarType = 'info',
  duration = 5000,
  options?: { achievementId?: AchievementId }
) => {
  useSnackbarStore.getState().addSnackbar(message, type, duration, options);
};
